// Re-export the Settings component as BrandingContent
// This wraps the existing Settings.jsx branding functionality
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Save,
  RotateCcw,
  DollarSign,
  Clock,
  Percent,
  Package,
  Building,
  Mail,
  Phone,
  Globe,
  Upload,
  ImageIcon,
  Trash2,
  Link2,
  Unlink,
  CheckCircle2,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Plus,
  Pencil,
  FileText,
  ChevronDown,
  ChevronUp,
  Palette,
  Zap,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import RichTextEditor from "@/components/RichTextEditor";
import EmailSignatureEditor from "@/components/EmailSignatureEditor";
import RateCalculatorTab from "@/components/settings/rate-calculator/RateCalculatorTab";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Settings config for pricing parameters
const SETTINGS_CONFIG = [
  { key: "labour_rate", label: "Labour Rate", type: "currency", suffix: "$/hr", step: 1, description: "Hourly rate for labour calculations", icon: DollarSign },
  { key: "garments_per_hour", label: "Garments/Hour", type: "number", suffix: "garments", step: 1, description: "Base garments printed per hour", icon: Clock },
  { key: "minimum_production_hours", label: "Min Labour Hours", type: "number", suffix: "hours", step: 0.25, description: "Minimum labour hours per job", icon: Clock },
  { key: "artwork_group_time_multiplier", label: "Art Group Time Add", type: "number", suffix: "hours", step: 0.25, description: "Additional time per artwork group", icon: Package },
  { key: "margin_percent", label: "Default Margin", type: "percent", suffix: "%", step: 1, description: "Default markup percentage", icon: Percent },
  { key: "gst_rate", label: "GST Rate", type: "percent", suffix: "%", step: 0.1, description: "Goods and Services Tax rate", icon: Percent },
  { key: "small_order_threshold", label: "Small Order Threshold", type: "number", suffix: "units", step: 1, description: "Orders under this quantity trigger surcharge", icon: Package },
  { key: "small_order_surcharge", label: "Small Order Surcharge", type: "currency", suffix: "", step: 1, description: "Extra charge for small orders (0 = disabled)", icon: DollarSign },
  { key: "default_lead_time_days", label: "Default Production Lead Time", type: "number", suffix: "days", step: 1, description: "Standard production lead time in days", icon: Clock },
];

// Sub-tabs within branding
const BRANDING_TABS = [
  { id: "pricing", label: "Pricing" },
  { id: "business", label: "Business Info" },
  { id: "integrations", label: "Integrations" },
  { id: "email", label: "Email" },
  { id: "rate-calculator", label: "Rate Calculator" },
];

// Tabs only Admins may see (also enforced server-side via require_admin)
const ADMIN_ONLY_TABS = ["rate-calculator"];

export default function BrandingContent() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("pricing");
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const logoInputRef = useRef(null);
  const emailLogoInputRef = useRef(null);
  const headerLogoInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingEmailLogo, setIsUploadingEmailLogo] = useState(false);
  const [isUploadingHeaderLogo, setIsUploadingHeaderLogo] = useState(false);
  const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);
  const [branding, setBranding] = useState({ header_logo: null, favicon: null });

  // Xero state
  const [xeroStatus, setXeroStatus] = useState({ connected: false, has_credentials: false });
  const [xeroClientId, setXeroClientId] = useState("");
  const [xeroClientSecret, setXeroClientSecret] = useState("");
  const [showXeroSecret, setShowXeroSecret] = useState(false);
  const [isConnectingXero, setIsConnectingXero] = useState(false);
  const [isDisconnectingXero, setIsDisconnectingXero] = useState(false);
  const [isSavingXeroCredentials, setIsSavingXeroCredentials] = useState(false);

  // Google state
  const [googleStatus, setGoogleStatus] = useState({ connected: false, has_credentials: false });
  const [gmailAccounts, setGmailAccounts] = useState([]); // All configured Gmail accounts with status
  const [googleClientId, setGoogleClientId] = useState("");
  const [googleClientSecret, setGoogleClientSecret] = useState("");
  const [showGoogleSecret, setShowGoogleSecret] = useState(false);
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);
  const [isDisconnectingGoogle, setIsDisconnectingGoogle] = useState(false);
  const [isSavingGoogleCredentials, setIsSavingGoogleCredentials] = useState(false);

  // Email state
  const [signatureAccounts, setSignatureAccounts] = useState([]);
  const [emailSignatures, setEmailSignatures] = useState({});
  const [isSavingSignatures, setIsSavingSignatures] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);

  const templatePlaceholders = ["job_name", "customer_name", "quote_total", "job_link", "your_name"];

  const getToken = () => localStorage.getItem("auth_token");
  const xeroRedirectUri = `${window.location.origin}/api/xero/callback`;
  const googleRedirectUri = `${window.location.origin}/api/google/callback`;

  useEffect(() => {
    loadSettings();
    loadBranding();
    loadXeroStatus();
    loadGoogleStatus();
    loadEmailSignatures();
    loadEmailTemplates();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/settings`, { headers });
      setSettings(res.data);
      setOriginalSettings(res.data);
    } catch (error) {
      console.error("Failed to load settings", error);
      toast.error("Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  };

  const loadBranding = async () => {
    try {
      const res = await axios.get(`${API}/branding`);
      setBranding(res.data || {});
      
      // Apply favicon dynamically if set
      if (res.data?.favicon) {
        const link = document.querySelector("link[rel~='icon']") || document.createElement("link");
        link.rel = "icon";
        link.href = res.data.favicon;
        document.head.appendChild(link);
      }
    } catch (error) {
      console.error("Failed to load branding", error);
    }
  };

  const handleUploadHeaderLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ["image/png", "image/svg+xml", "image/jpeg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload PNG, SVG, JPEG, or WebP file");
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be under 2MB");
      return;
    }
    
    setIsUploadingHeaderLogo(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API}/branding/header-logo`, formData);
      toast.success("Header logo uploaded successfully");
      loadBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setIsUploadingHeaderLogo(false);
      if (headerLogoInputRef.current) headerLogoInputRef.current.value = "";
    }
  };

  const handleUploadFavicon = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ["image/png", "image/x-icon", "image/vnd.microsoft.icon"];
    if (!allowedTypes.includes(file.type) && !file.name.endsWith(".ico")) {
      toast.error("Please upload PNG or ICO file");
      return;
    }
    
    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error("Favicon must be under 500KB");
      return;
    }
    
    setIsUploadingFavicon(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API}/branding/favicon`, formData);
      toast.success("Favicon uploaded successfully");
      loadBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload favicon");
    } finally {
      setIsUploadingFavicon(false);
      if (faviconInputRef.current) faviconInputRef.current.value = "";
    }
  };

  const handleUploadPdfLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    
    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      toast.error("File size must be under 1MB");
      return;
    }
    
    setIsUploadingLogo(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/settings/logo`, formData, { 
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      toast.success("PDF logo uploaded successfully");
      loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleRemovePdfLogo = () => {
    setSettings(prev => ({ ...prev, business_logo_url: "" }));
    // Note: This change will be persisted when user clicks Save
    toast.info("Click Save to confirm logo removal");
  };

  const handleUploadEmailLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    
    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      toast.error("File size must be under 1MB");
      return;
    }
    
    setIsUploadingEmailLogo(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/settings/email-logo`, formData, { 
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      toast.success("Email logo uploaded successfully");
      loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload email logo");
    } finally {
      setIsUploadingEmailLogo(false);
      if (emailLogoInputRef.current) emailLogoInputRef.current.value = "";
    }
  };

  const handleRemoveEmailLogo = () => {
    setSettings(prev => ({ ...prev, email_logo_url: "" }));
    toast.info("Click Save to confirm logo removal");
  };

  const loadXeroStatus = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/xero/status`, { headers });
      setXeroStatus(res.data);
    } catch (error) {
      console.error("Failed to load Xero status", error);
    }
  };

  const loadGoogleStatus = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/google/status`, { headers });
      setGoogleStatus(res.data);
      
      // Also load all Gmail accounts with their connection status
      const accountsRes = await axios.get(`${API}/gmail/accounts`, { headers });
      setGmailAccounts(accountsRes.data.accounts || []);
    } catch (error) {
      console.error("Failed to load Google status", error);
    }
  };

  const loadEmailSignatures = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/settings/email-signatures`, { headers });
      setSignatureAccounts(res.data.accounts || []);
      setEmailSignatures(res.data.signatures || {});
    } catch (error) {
      console.error("Failed to load email signatures", error);
    }
  };

  const loadEmailTemplates = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/settings/email-templates`, { headers });
      setEmailTemplates(res.data.templates || res.data || []);
    } catch (error) {
      console.error("Failed to load email templates", error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/settings`, settings, { headers });
      setOriginalSettings(settings);
      toast.success("Settings saved");
    } catch (error) {
      console.error("Failed to save settings", error);
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const updateSetting = (key, value, type) => {
    let parsedValue = value;
    if (type === "currency" || type === "number") {
      parsedValue = parseFloat(value) || 0;
    } else if (type === "percent") {
      parsedValue = parseFloat(value) || 0;
    }
    setSettings(prev => ({ ...prev, [key]: parsedValue }));
  };

  const applySuggestedLabourRate = (rate) => {
    setSettings(prev => ({ ...prev, labour_rate: rate }));
    setActiveTab("pricing");
    toast.info(`Labour Rate pre-filled with $${rate.toFixed(2)}/hr — review it and click Save to apply.`);
  };

  const getDisplayValue = (key, type) => {
    const val = settings?.[key];
    if (val === undefined || val === null) return "";
    return val;
  };

  const copyRedirectUri = () => {
    navigator.clipboard.writeText(xeroRedirectUri);
    toast.success("Redirect URI copied");
  };

  const copyGoogleRedirectUri = () => {
    navigator.clipboard.writeText(googleRedirectUri);
    toast.success("Redirect URI copied");
  };

  const saveXeroCredentials = async () => {
    setIsSavingXeroCredentials(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/xero/credentials`, {
        client_id: xeroClientId,
        client_secret: xeroClientSecret,
      }, { headers });
      toast.success("Xero credentials saved");
      loadXeroStatus();
    } catch (error) {
      toast.error("Failed to save Xero credentials");
    } finally {
      setIsSavingXeroCredentials(false);
    }
  };

  const connectXero = async () => {
    setIsConnectingXero(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/xero/auth-url`, { headers });
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      } else {
        toast.error("Invalid auth URL received");
        setIsConnectingXero(false);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to start Xero connection";
      toast.error(errorMsg);
      console.error("Xero connection error:", error.response?.data || error);
      setIsConnectingXero(false);
    }
  };

  const disconnectXero = async () => {
    setIsDisconnectingXero(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/xero/disconnect`, {}, { headers });
      toast.success("Xero disconnected");
      loadXeroStatus();
    } catch (error) {
      toast.error("Failed to disconnect Xero");
    } finally {
      setIsDisconnectingXero(false);
    }
  };

  const saveGoogleCredentials = async () => {
    setIsSavingGoogleCredentials(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/google/credentials`, {
        client_id: googleClientId,
        client_secret: googleClientSecret,
      }, { headers });
      toast.success("Google credentials saved");
      loadGoogleStatus();
    } catch (error) {
      toast.error("Failed to save Google credentials");
    } finally {
      setIsSavingGoogleCredentials(false);
    }
  };

  const connectGoogle = async () => {
    setIsConnectingGoogle(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/google/auth`, { headers });
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error("Failed to start Google connection");
      setIsConnectingGoogle(false);
    }
  };

  const disconnectGoogle = async () => {
    setIsDisconnectingGoogle(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/google/disconnect`, {}, { headers });
      toast.success("Google disconnected");
      loadGoogleStatus();
    } catch (error) {
      toast.error("Failed to disconnect Google");
    } finally {
      setIsDisconnectingGoogle(false);
    }
  };

  const saveEmailSignatures = async () => {
    setIsSavingSignatures(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/settings/email-signatures`, emailSignatures, { headers });
      toast.success("Email signatures saved");
    } catch (error) {
      toast.error("Failed to save email signatures");
    } finally {
      setIsSavingSignatures(false);
    }
  };

  const handleSaveTemplate = async (template) => {
    setIsSavingTemplate(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      if (template.id) {
        await axios.put(`${API}/settings/email-templates/${template.id}`, template, { headers });
      } else {
        await axios.post(`${API}/settings/email-templates`, template, { headers });
      }
      toast.success("Template saved");
      setShowTemplateModal(false);
      loadEmailTemplates();
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/settings/email-templates/${templateId}`, { headers });
      toast.success("Template deleted");
      setDeleteTemplateId(null);
      loadEmailTemplates();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const hasChanges = settings && originalSettings && 
    JSON.stringify(settings) !== JSON.stringify(originalSettings);

  if (isLoading) {
    return (
      <div className="p-6 text-center py-12 text-gray-400">
        <div className="animate-pulse">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Palette className="w-5 h-5 text-pink-600" />
            Branding & Integrations
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Configure pricing, branding, and service connections
          </p>
        </div>
        {activeTab === "pricing" && (
          <Button size="sm" onClick={handleSave} disabled={isSaving || !hasChanges} className="h-9 text-xs">
            <Save className="w-3 h-3 mr-1.5" />
            {isSaving ? "Saving..." : hasChanges ? "Save*" : "Save"}
          </Button>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {BRANDING_TABS.filter(tab => isAdmin || !ADMIN_ONLY_TABS.includes(tab.id)).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`branding-tab-${tab.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-pink-600 text-pink-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Pricing Tab */}
      {activeTab === "pricing" && (
        <div className="grid grid-cols-2 gap-4 max-w-3xl">
          {SETTINGS_CONFIG.map((config) => {
            const Icon = config.icon;
            return (
              <div key={config.key} className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-4 h-4 text-gray-400" />
                  <Label className="text-sm font-medium text-gray-700">{config.label}</Label>
                </div>
                <p className="text-xs text-gray-500 mb-3">{config.description}</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step={config.step}
                    value={getDisplayValue(config.key, config.type)}
                    onChange={(e) => updateSetting(config.key, e.target.value, config.type)}
                    className="font-mono max-w-[120px] h-9 text-sm border-gray-200"
                  />
                  <span className="text-xs text-gray-500">{config.suffix}</span>
                </div>
                {config.key === "labour_rate" && isAdmin && (
                  <button
                    type="button"
                    onClick={() => setActiveTab("rate-calculator")}
                    className="mt-2 text-xs font-medium text-pink-600 hover:text-pink-700"
                    data-testid="open-rate-calculator-link"
                  >
                    Open Rate Calculator →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Rate Calculator Tab — kept mounted so unsaved calculator edits survive tab switches */}
      {isAdmin && (
        <div className={activeTab === "rate-calculator" ? "" : "hidden"}>
          <RateCalculatorTab currentLabourRate={originalSettings?.labour_rate} onApply={applySuggestedLabourRate} />
        </div>
      )}

      {/* Business Info Tab */}
      {activeTab === "business" && (
        <div className="space-y-4 max-w-2xl">
          <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
            <div className="flex items-center gap-2 mb-2">
              <Building className="w-4 h-4 text-gray-400" />
              <Label className="text-sm font-medium text-gray-700">Business Name</Label>
            </div>
            <Input
              value={settings?.business_name || ""}
              onChange={(e) => updateSetting("business_name", e.target.value, "text")}
              placeholder="Your Business Name"
              className="h-9 text-sm border-gray-200"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <Label className="text-sm font-medium text-gray-700">Email</Label>
              </div>
              <Input
                type="email"
                value={settings?.business_email || ""}
                onChange={(e) => updateSetting("business_email", e.target.value, "text")}
                placeholder="quotes@example.com"
                className="h-9 text-sm border-gray-200"
              />
            </div>
            <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2 mb-2">
                <Phone className="w-4 h-4 text-gray-400" />
                <Label className="text-sm font-medium text-gray-700">Phone</Label>
              </div>
              <Input
                value={settings?.business_phone || ""}
                onChange={(e) => updateSetting("business_phone", e.target.value, "text")}
                placeholder="0400 000 000"
                className="h-9 text-sm border-gray-200"
              />
            </div>
          </div>
          <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-gray-400" />
              <Label className="text-sm font-medium text-gray-700">Website</Label>
            </div>
            <Input
              value={settings?.business_website || ""}
              onChange={(e) => updateSetting("business_website", e.target.value, "text")}
              placeholder="www.example.com"
              className="h-9 text-sm border-gray-200"
            />
          </div>

          {/* Public App URL */}
          <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
            <div className="flex items-center gap-2 mb-2">
              <Link2 className="w-4 h-4 text-gray-400" />
              <Label className="text-sm font-medium text-gray-700">Public App URL</Label>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Base URL for client-facing links in emails. Leave blank to auto-detect.
            </p>
            <Input
              value={settings?.public_app_url || ""}
              onChange={(e) => updateSetting("public_app_url", e.target.value, "text")}
              placeholder={window.location.origin}
              className="h-9 text-sm border-gray-200"
            />
            <p className="text-xs text-gray-400 mt-2">
              Current: {settings?.public_app_url || window.location.origin}
            </p>
          </div>

          {/* Brand Assets - All logos in one place */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-gray-400" />
              Brand Assets
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Centralized branding assets used across the app, PDFs, and emails.
            </p>
            
            {/* PDF Logo - Full width */}
            <div className="p-4 rounded-lg border border-gray-100 bg-white mb-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-gray-400" />
                <Label className="text-sm font-medium text-gray-700">PDF / Quote Logo</Label>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Used in PDF quote headers and email signatures. PNG or JPG, max 1MB.
              </p>
              <div className="flex items-center gap-4">
                {settings?.business_logo_url ? (
                  <div className="flex items-center gap-3">
                    <div className="h-14 w-28 border rounded bg-white flex items-center justify-center overflow-hidden">
                      <img 
                        src={settings.business_logo_url} 
                        alt="PDF Logo" 
                        className="max-h-12 max-w-24 object-contain"
                      />
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => logoInputRef.current?.click()}
                        className="h-7 text-xs"
                      >
                        Replace
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRemovePdfLogo}
                        className="text-red-600 hover:text-red-700 h-7 text-xs"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={isUploadingLogo}
                      className="h-8 text-xs"
                      data-testid="upload-pdf-logo-btn"
                    >
                      <Upload className="w-3 h-3 mr-1.5" />
                      {isUploadingLogo ? "Uploading..." : "Upload"}
                    </Button>
                    <span className="text-xs text-gray-400">No logo uploaded</span>
                  </div>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleUploadPdfLogo}
                  className="hidden"
                />
              </div>
            </div>

            {/* Email Signature Logo */}
            <div className="p-4 rounded-lg border border-gray-100 bg-white mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <Label className="text-sm font-medium text-gray-700">Email Signature Logo</Label>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Used in email signatures. Recommended: horizontal format, ~120–180px wide. PNG or JPG, max 1MB.
                {!settings?.email_logo_url && settings?.business_logo_url && (
                  <span className="text-gray-400 ml-1">(Using PDF logo as fallback)</span>
                )}
              </p>
              <div className="flex items-center gap-4">
                {settings?.email_logo_url ? (
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-32 border rounded bg-white flex items-center justify-center overflow-hidden">
                      <img 
                        src={settings.email_logo_url} 
                        alt="Email Logo" 
                        className="max-h-10 max-w-28 object-contain"
                      />
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => emailLogoInputRef.current?.click()}
                        className="h-7 text-xs"
                      >
                        Replace
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRemoveEmailLogo}
                        className="text-red-600 hover:text-red-700 h-7 text-xs"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => emailLogoInputRef.current?.click()}
                      disabled={isUploadingEmailLogo}
                      className="h-8 text-xs"
                      data-testid="upload-email-logo-btn"
                    >
                      <Upload className="w-3 h-3 mr-1.5" />
                      {isUploadingEmailLogo ? "Uploading..." : "Upload"}
                    </Button>
                    {settings?.business_logo_url ? (
                      <span className="text-xs text-gray-400">Using PDF logo</span>
                    ) : (
                      <span className="text-xs text-gray-400">No logo</span>
                    )}
                  </div>
                )}
                <input
                  ref={emailLogoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleUploadEmailLogo}
                  className="hidden"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Header / Login Logo */}
              <div className="p-4 rounded-lg border border-gray-100 bg-white">
                <div className="flex items-center gap-2 mb-2">
                  <ImageIcon className="w-4 h-4 text-gray-400" />
                  <Label className="text-sm font-medium text-gray-700">App Header Logo</Label>
                </div>
                <p className="text-xs text-gray-500 mb-3">Header & login page. SVG or PNG, max 2MB</p>
                <div className="flex items-center gap-4">
                  {branding.header_logo ? (
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-24 border rounded bg-white flex items-center justify-center overflow-hidden">
                        <img 
                          src={branding.header_logo} 
                          alt="Header Logo" 
                          className="max-h-8 max-w-20 object-contain"
                        />
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => headerLogoInputRef.current?.click()}
                          className="h-7 text-xs"
                        >
                          Replace
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              await axios.delete(`${API}/branding/header-logo`);
                              toast.success("Header logo removed");
                              loadBranding();
                            } catch (error) {
                              toast.error("Failed to remove logo");
                            }
                          }}
                          className="text-red-600 hover:text-red-700 h-7 text-xs"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => headerLogoInputRef.current?.click()}
                        disabled={isUploadingHeaderLogo}
                        className="h-8 text-xs"
                        data-testid="upload-header-logo-btn"
                      >
                        <Upload className="w-3 h-3 mr-1.5" />
                        {isUploadingHeaderLogo ? "Uploading..." : "Upload"}
                      </Button>
                      <span className="text-xs text-gray-400">No logo</span>
                    </div>
                  )}
                  <input
                    ref={headerLogoInputRef}
                    type="file"
                    accept="image/png,image/svg+xml,image/jpeg,image/webp"
                    onChange={handleUploadHeaderLogo}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Favicon */}
              <div className="p-4 rounded-lg border border-gray-100 bg-white">
                <div className="flex items-center gap-2 mb-2">
                  <ImageIcon className="w-4 h-4 text-gray-400" />
                  <Label className="text-sm font-medium text-gray-700">Favicon</Label>
                </div>
                <p className="text-xs text-gray-500 mb-3">Browser tabs. PNG or ICO, max 500KB</p>
                <div className="flex items-center gap-4">
                  {branding.favicon ? (
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 border rounded bg-white flex items-center justify-center overflow-hidden">
                        <img 
                          src={branding.favicon} 
                          alt="Favicon" 
                          className="max-h-6 max-w-6 object-contain"
                        />
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => faviconInputRef.current?.click()}
                          className="h-7 text-xs"
                        >
                          Replace
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              await axios.delete(`${API}/branding/favicon`);
                              toast.success("Favicon removed");
                              loadBranding();
                              // Reset to default
                              const link = document.querySelector("link[rel~='icon']");
                              if (link) link.href = "/favicon.ico";
                            } catch (error) {
                              toast.error("Failed to remove favicon");
                            }
                          }}
                          className="text-red-600 hover:text-red-700 h-7 text-xs"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => faviconInputRef.current?.click()}
                        disabled={isUploadingFavicon}
                        className="h-8 text-xs"
                        data-testid="upload-favicon-btn"
                      >
                        <Upload className="w-3 h-3 mr-1.5" />
                        {isUploadingFavicon ? "Uploading..." : "Upload"}
                      </Button>
                      <span className="text-xs text-gray-400">No favicon</span>
                    </div>
                  )}
                  <input
                    ref={faviconInputRef}
                    type="file"
                    accept="image/png,image/x-icon,image/vnd.microsoft.icon,.ico"
                    onChange={handleUploadFavicon}
                    className="hidden"
                  />
                </div>
              </div>
            </div>
          </div>

          <Button size="sm" onClick={handleSave} disabled={isSaving} className="h-9 text-xs">
            <Save className="w-3 h-3 mr-1.5" />
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      )}

      {/* Integrations Tab */}
      {activeTab === "integrations" && (
        <div className="space-y-6 max-w-2xl">
          {/* Xero */}
          <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-blue-500" />
              Xero Integration
            </h3>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-2.5 h-2.5 rounded-full ${xeroStatus.connected ? 'bg-green-500' : xeroStatus.needs_reconnect ? 'bg-amber-500' : 'bg-gray-300'}`}></div>
              <span className="text-sm">
                {xeroStatus.connected 
                  ? `Connected to ${xeroStatus.tenant_name || 'Xero'}` 
                  : xeroStatus.needs_reconnect 
                    ? `Session Expired - ${xeroStatus.tenant_name || 'Xero'}` 
                    : 'Not Connected'}
              </span>
            </div>
            {xeroStatus.needs_reconnect && (
              <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                <AlertCircle className="w-3 h-3 inline-block mr-1" />
                Your Xero session has expired. Please reconnect to continue creating invoices.
              </div>
            )}
            {xeroStatus.connected ? (
              <Button
                variant="outline"
                size="sm"
                onClick={disconnectXero}
                disabled={isDisconnectingXero}
                className="h-8 text-xs text-red-600"
              >
                <Unlink className="w-3 h-3 mr-1.5" />
                {isDisconnectingXero ? "Disconnecting..." : "Disconnect"}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={connectXero}
                disabled={isConnectingXero || !xeroStatus.has_credentials}
                className={`h-8 text-xs ${xeroStatus.needs_reconnect ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-500 hover:bg-blue-600'}`}
              >
                <Link2 className="w-3 h-3 mr-1.5" />
                {isConnectingXero ? "Connecting..." : xeroStatus.needs_reconnect ? "Reconnect to Xero" : "Connect to Xero"}
              </Button>
            )}
          </div>

          {/* Google */}
          <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4 text-red-500" />
              Google / Gmail Integration
            </h3>
            
            {/* Email Accounts List */}
            {gmailAccounts.length > 0 ? (
              <div className="space-y-2 mb-3">
                {gmailAccounts.map((account) => (
                  <div key={account.email} className="flex items-center gap-3 p-2 rounded bg-white border border-gray-100">
                    <div className={`w-2 h-2 rounded-full ${account.connected ? 'bg-green-500' : 'bg-amber-400'}`}></div>
                    <span className="text-sm font-medium text-gray-900">{account.email}</span>
                    <span className={`text-xs ${account.connected ? 'text-green-600' : 'text-amber-600'}`}>
                      {account.connected ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-2.5 h-2.5 rounded-full ${googleStatus.connected ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className="text-sm">
                  {googleStatus.connected ? `Connected as ${googleStatus.email || 'Google'}` : 'Not Connected'}
                </span>
              </div>
            )}
            
            {googleStatus.connected ? (
              <Button
                variant="outline"
                size="sm"
                onClick={disconnectGoogle}
                disabled={isDisconnectingGoogle}
                className="h-8 text-xs text-red-600"
              >
                <Unlink className="w-3 h-3 mr-1.5" />
                {isDisconnectingGoogle ? "Disconnecting..." : "Disconnect All"}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={connectGoogle}
                disabled={isConnectingGoogle || !googleStatus.has_credentials}
                className="h-8 text-xs bg-red-500 hover:bg-red-600"
                data-testid="google-connect-btn"
              >
                <Link2 className="w-3 h-3 mr-1.5" />
                {isConnectingGoogle ? "Connecting..." : "Connect to Google"}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Email Tab */}
      {activeTab === "email" && (
        <div className="space-y-6 max-w-4xl">
          {/* Signatures */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-medium text-gray-700">Email Signatures</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Use templates for quick setup or HTML for full customization
                </p>
              </div>
            </div>
            {signatureAccounts.length === 0 ? (
              <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50 text-center text-gray-400">
                <Mail className="w-6 h-6 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Connect Gmail to add signatures</p>
              </div>
            ) : (
              <div className="space-y-6">
                {signatureAccounts.map((email) => (
                  <div key={email}>
                    <EmailSignatureEditor
                      value={emailSignatures[email] || ""}
                      onChange={(value) => setEmailSignatures(prev => ({ ...prev, [email]: value }))}
                      email={email}
                      showPreview={true}
                      businessSettings={{
                        business_name: settings?.business_name || "Alpine Armour",
                        business_phone: settings?.business_phone || "",
                        business_website: settings?.business_website || "",
                        business_logo_url: settings?.business_logo_url || "",
                        email_logo_url: settings?.email_logo_url || "",
                      }}
                      previewVariables={{
                        title: "Your Title",
                      }}
                    />
                  </div>
                ))}
                <Button size="sm" onClick={saveEmailSignatures} disabled={isSavingSignatures} className="h-9 text-xs">
                  <Save className="w-3 h-3 mr-1.5" />
                  {isSavingSignatures ? "Saving..." : "Save Signatures"}
                </Button>
              </div>
            )}
          </div>

          {/* Templates */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">Email Templates</h3>
              <Button
                size="sm"
                onClick={() => {
                  setEditingTemplate({ name: "", subject: "", body: "", type: "general" });
                  setShowTemplateModal(true);
                }}
                className="h-8 text-xs"
              >
                <Plus className="w-3 h-3 mr-1.5" />
                New
              </Button>
            </div>
            {emailTemplates.length === 0 ? (
              <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50 text-center text-gray-400">
                <FileText className="w-6 h-6 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No templates yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {emailTemplates.map((template) => (
                  <div key={template.id} className="p-3 rounded-lg border border-gray-100 bg-gray-50/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{template.name}</span>
                        <Badge variant="outline" className="text-[10px]">{template.type}</Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingTemplate(template);
                            setShowTemplateModal(true);
                          }}
                          className="h-7 w-7 p-0"
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTemplateId(template.id)}
                          className="h-7 w-7 p-0 text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Template Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingTemplate?.id ? "Edit Template" : "New Template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editingTemplate?.name || ""}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Template name"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={editingTemplate?.type || "general"}
                  onValueChange={(value) => setEditingTemplate(prev => ({ ...prev, type: value }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="quote">Quote</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="follow-up">Follow-up</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={editingTemplate?.subject || ""}
                onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Email subject"
              />
            </div>
            <div className="space-y-2">
              <Label>Body (HTML)</Label>
              <textarea
                value={editingTemplate?.body || ""}
                onChange={(e) => setEditingTemplate(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Enter email template HTML..."
                className="w-full min-h-[200px] p-3 font-mono text-xs bg-gray-900 text-green-400 rounded-md border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                spellCheck={false}
              />
              <p className="text-xs text-gray-500">
                Available variables: {"{{job_name}}"}, {"{{customer_name}}"}, {"{{quote_total}}"}, {"{{job_link}}"}, {"{{your_name}}"}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateModal(false)}>Cancel</Button>
            <Button onClick={() => handleSaveTemplate(editingTemplate)} disabled={isSavingTemplate}>
              {isSavingTemplate ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Template Confirm */}
      <AlertDialog open={!!deleteTemplateId} onOpenChange={() => setDeleteTemplateId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteTemplate(deleteTemplateId)} className="bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
